import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import DataGridLoader from '../Common/DataGridLoader';
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from '@mui/icons-material/Check';
import { CommonService } from "../../Service/CommonService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateRange } from "react-date-range";
import Dropdown from "react-bootstrap/Dropdown";
import { Service } from "../../Service/ApiService";
import MultiSelectChipInput from "../Common/MultiSelectChipInput";
import CloseIcon from "@mui/icons-material/Close";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useHistory } from "react-router-dom";
import { Accordion } from "react-bootstrap";

const CompanyBulkInventoryUploadReport: React.FC = () => {
    const history = useHistory();
    const previousMonthRange = CommonService.getPreviousMonthDateRange();
    const [reportList, setReportList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState("uploadDate");
    const [sortDirection, setSortDirection] = useState("desc");
    const controllerRef = useRef<AbortController | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const datePickerRef = useRef<HTMLDivElement | null>(null);
    const mainInputRef = useRef<HTMLDivElement | null>(null);
    const [mainPickerStyle, setMainPickerStyle] = useState<React.CSSProperties>({});
    const [isMainDatePickerOpen, setIsMainDatePickerOpen] = useState(false);
    const [filter, setFilter] = useState<any>(() => ({
        companyName: null,
        startDate: previousMonthRange.startDateText,
        endDate: previousMonthRange.endDateText
    }));
    // final selected values — pre-fill with previous month so the picker shows it on first open
    const [selectedDateRange, setSelectedDateRange] = useState<any>({
        startDate: previousMonthRange.startDateText ? new Date(previousMonthRange.startDateText + "T00:00:00") : null,
        endDate: previousMonthRange.endDateText ? new Date(previousMonthRange.endDateText + "T00:00:00") : null,
        key: "selection",
    });
    // temp values for picker
    const [tempRange, setTempRange] = useState<any>({
        startDate: previousMonthRange.startDateText ? new Date(previousMonthRange.startDateText + "T00:00:00") : null,
        endDate: previousMonthRange.endDateText ? new Date(previousMonthRange.endDateText + "T00:00:00") : null,
        key: "selection",
    });

    const [allCompanyOptions, setAllCompanyOptions] = useState<string[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

    const localeText = {
        noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
    };

    const formatDate = (date: Date | null) => {
        if (!date) return null;
        const parsedDate =
            typeof date === "string"
                ? new Date(date + "T00:00:00")
                : date;
        return parsedDate.toLocaleDateString("en-CA");
    };

    useEffect(() => {
        getCompanyBulkInventoryUploadReport(page, pageSize, filter, sortField, sortDirection);
        Service.fetchCompaniesList()
            .then((res: any) => {
                const names: string[] = (res.data || []).map((c: any) => c.companyName).filter(Boolean).sort();
                setAllCompanyOptions(names);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                isMainDatePickerOpen &&
                datePickerRef.current &&
                !datePickerRef.current.contains(target) &&
                mainInputRef.current &&
                !mainInputRef.current.contains(target)
            ) {
                setIsMainDatePickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, [isMainDatePickerOpen]);

    useEffect(() => {
        const handleScroll = () => { updatePickerPositions(); };
        const handleResize = () => { updatePickerPositions(); };
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
        };
    }, [isMainDatePickerOpen]);

    const CellWithTooltip = ({ value }: { value: string | number }) => (
        <Tooltip arrow title={value?.toString()}>
            <span>{value?.toString()}</span>
        </Tooltip>
    );

    const getCompanyBulkInventoryUploadReport = async (page, size, filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;
        try {
            setIsLoading(true);
            setReportList([]);
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
            controllerRef.current = controller;
            const response: any = await InventoryApiService.getCompanyBulkInventoryUploadReport(page, size, filter, sortField, sortDirection, signal);
            setReportList(response.data.content);
            setTotalCount(response.data.totalElements);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const exportCompanyBulkInventoryUploadReport = async (filter: any, sortField: any, sortDirection: any) => {
        const controller = new AbortController();
        const signal = controller.signal;
        try {
            setIsLoading(true);
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
            controllerRef.current = controller;
            InventoryApiService.exportCompanyBulkInventoryUploadReport(filter, sortField, sortDirection, signal);
            CommonService.Toast.fire({
                title: "Your export is in progress. We'll notify you when it's ready!",
                icon: "success",
            });
            setIsLoading(false);
        } catch (error) {
            console.error("Error exporting data:", error);
            setIsLoading(false);
        }
    };

    const tableDataWithIds = reportList.map((rowData) => ({ ...rowData }));

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort;
            setSortField(newSortField);
            setSortDirection(newSortDirection);
            getCompanyBulkInventoryUploadReport(page, pageSize, filter, newSortField, newSortDirection);
        }
    };

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);
        getCompanyBulkInventoryUploadReport(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
    };

    const handleFilterReset = (field: keyof any) => {
        if (field === 'companyName') setSelectedCompanies([]);
        const tempFilter = { ...filter, [field]: null };
        setFilter(tempFilter);
    };

    const handleClear = () => {
        handleMainClear();
        const tempFilter = { companyName: null, startDate: null, endDate: null };
        setFilter(tempFilter);
        setSelectedCompanies([]);
        getCompanyBulkInventoryUploadReport(page, pageSize, tempFilter, sortField, sortDirection);
    };

    const handleApply = () => {
        const merged = {
            ...filter,
            companyName: selectedCompanies.length > 0 ? selectedCompanies.join(",") : null,
        };
        setFilter(merged);
        getCompanyBulkInventoryUploadReport(page, pageSize, merged, sortField, sortDirection);
    };

    // main date picker actions
    const handleMainClear = () => {
        setTempRange({ startDate: null, endDate: null, key: "selection" });
        setSelectedDateRange({ startDate: null, endDate: null, key: "selection" });
        setIsMainDatePickerOpen(false);
        const tempFilter = { ...filter, startDate: null, endDate: null };
        setFilter(tempFilter);
    };

    const getFixedPickerPosition = (
        triggerEl: HTMLDivElement | null,
        pickerWidth: number = 700,
        gap: number = 8
    ) => {
        if (!triggerEl) return {};
        const rect = triggerEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        let left = rect.left;
        let top = rect.bottom + gap;
        if (left + pickerWidth > viewportWidth - 16) {
            left = viewportWidth - pickerWidth - 16;
        }
        if (left < 16) left = 16;
        return { position: "fixed" as const, top: `${top}px`, left: `${left}px`, zIndex: 9999 };
    };

    const handleMainPickerToggle = () => {
        if (!isMainDatePickerOpen) {
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        }
        setIsMainDatePickerOpen((prev) => !prev);
    };

    const updatePickerPositions = () => {
        if (isMainDatePickerOpen) {
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        }
    };

    const handleTempChange = (start: Date | null, end: Date | null) => {
        setTempRange({ startDate: start, endDate: end, key: "selection" });
    };

    const handleMainCancel = () => {
        setTempRange(selectedDateRange);
        setIsMainDatePickerOpen(false);
    };

    const handleMainOk = () => {
        setSelectedDateRange(tempRange);
        setIsMainDatePickerOpen(false);
        const tempFilter = {
            ...filter,
            startDate: formatDate(tempRange.startDate),
            endDate: formatDate(tempRange.endDate),
        };
        setFilter(tempFilter);
    };

    const columns: GridColDef[] = [
        {
            field: "companyName",
            headerName: "Account Owner Name",
            flex: 1,
            minWidth: 200,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.companyName ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.row.companyName} />
            ),
        },
        {
            field: "uploadDate",
            headerName: "Upload Date",
            flex: 1,
            minWidth: 160,
            sortable: true,
            valueGetter: (value: any, row: any) => {
                if (!row.uploadDate) return "";

                const [year, month, day] = row.uploadDate.split("-");

                return new Date(
                    Number(year),
                    Number(month) - 1,
                    Number(day)
                ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                });
            },
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "-"} />
            ),
        },
        {
            field: "requestNumber",
            headerName: "Request #",
            flex: 1,
            minWidth: 160,
            sortable: false,
            valueGetter: (value: any, row: any) => `${row.requestNumber ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <div className="d-flex align-items-center h-100">
                    <Tooltip arrow title={params.value || "-"}>
                        <span
                            style={{ color: "#1976d2", cursor: "pointer", textDecoration: "none" }}
                            onClick={() => {
                                if (params.row.id) {
                                    history.push(`/master/view-customer-service-request/${params.row.id}`);
                                }
                            }}
                        >
                            {params.value || "-"}
                        </span>
                    </Tooltip>
                </div>
            ),
        },
        {
            field: "uploadCount",
            headerName: "Success Count",
            flex: 1,
            minWidth: 160,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.uploadCount ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "0"} />
            ),
        },
        {
            field: "failureCount",
            headerName: "Failure Count",
            flex: 1,
            minWidth: 160,
            sortable: true,
            valueGetter: (value: any, row: any) => `${row.failureCount ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "0"} />
            ),
        },
        {
            field: "totalCount",
            headerName: "Total Count",
            flex: 1,
            minWidth: 160,
            sortable: false,
            valueGetter: (value: any, row: any) => `${row.totalCount ?? ""}`,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
                <div>
                    <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        {params.colDef.headerName}
                    </Typography>
                </div>
            ),
            renderCell: (params: any) => (
                <CellWithTooltip value={params.value || "0"} />
            ),
        },
        
    ];

    return (<>
        <div className="reporting-filter-btn-wrapper company-bulk-rpt">
            {reportList.length > 0 || isLoading ?
                <>
                    <button title="Export to Excel"
                        className="primary-btn d-inline-flex align-items-center py-1 export-btn"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            exportCompanyBulkInventoryUploadReport(filter, sortField, sortDirection);
                        }}
                    >
                        <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                        Export
                    </button>
                </>
                : null}
        </div>
        <div className="user-table-container report-module">
                <Accordion defaultActiveKey="0" className="filter-accordion">
                    <Accordion.Item eventKey="0" className="filter-accordion-item">
                        <Accordion.Header className="filter-accordion-header">
                            <span className="accordion-filter-title">Filters</span>
                            <button
                                className="filter-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                Reset All
                            </button>
                        </Accordion.Header>
                        <Accordion.Body className="filter-accordion-body p-0">
                            <div className="filter-panel-accordion">

                                <div className="filter-body">
                                    {/* Date range */}
                                    <div className="filter-section position-relative">
                                        <div className="section-header">
                                            <h4>Date range</h4>
                                            <button className="section-btn" type="button" onClick={handleMainClear}>Reset</button>
                                        </div>
                                        <div className="date-input-wrapper" ref={mainInputRef}>
                                            <input
                                                type="text"
                                                placeholder="Select date range"
                                                readOnly
                                                className="form-control"
                                                value={
                                                    selectedDateRange.startDate && selectedDateRange.endDate
                                                        ? `${formatDate(selectedDateRange.startDate)} - ${formatDate(selectedDateRange.endDate)}`
                                                        : ""
                                                }
                                                onClick={handleMainPickerToggle}
                                            />
                                            <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                                            </svg>
                                        </div>

                                        {isMainDatePickerOpen && (
                                            <div className="datepicker-menu" ref={datePickerRef} style={mainPickerStyle}>
                                                <DateRange
                                                    ranges={[tempRange]}
                                                    editableDateInputs={true}
                                                    dropdownMode="scroll"
                                                    onChange={(item: any) =>
                                                        handleTempChange(
                                                            item.selection.startDate ?? null,
                                                            item.selection.endDate ?? null
                                                        )
                                                    }
                                                    moveRangeOnFirstSelection={false}
                                                    months={2}
                                                    direction="horizontal"
                                                    showDateDisplay={false}
                                                />
                                                <div className="datepicker-menu-bottom">
                                                    <button type="button" onClick={handleMainCancel} className="datepicker-btn">
                                                        CANCEL
                                                    </button>
                                                    <button type="button" onClick={handleMainOk} className="datepicker-btn">
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Company */}
                                    <div className="filter-section">
                                        <div className="section-header">
                                            <h4>Account Owner</h4>
                                            <button className="section-btn" type="button" onClick={() => handleFilterReset("companyName")}>Reset</button>
                                        </div>
                                        <MultiSelectChipInput
                                            selected={selectedCompanies}
                                            options={allCompanyOptions}
                                            placeholder="Select account owners"
                                            searchPlaceholder="Search account owner"
                                            onToggle={val => setSelectedCompanies(prev =>
                                                prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                            )}
                                            onSelectAll={() => setSelectedCompanies(prev =>
                                                prev.length === allCompanyOptions.length ? [] : [...allCompanyOptions]
                                            )}
                                            onReset={() => setSelectedCompanies([])}
                                        />
                                    </div>
                                </div>
                                <div className="filter-bottom">
                                    <div className="filter-actions">
                                        <button className="filter-btn" onClick={handleApply}><CheckIcon /> Apply</button>
                                    </div>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            <div className="userTable">
                <DataGrid
                    rows={tableDataWithIds}
                    columns={columns}
                    className="data-grid"
                    pagination
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={onPaginationModelChange}
                    rowCount={totalCount}
                    paginationMode="server"
                    sortingMode="server"
                    onSortModelChange={handleSortChange}
                    disableRowSelectionOnClick={true}
                    pageSizeOptions={[10, 20, 50, 100]}
                    localeText={localeText}
                    getRowId={(row) => row.id}
                    loading={isLoading}
                    slots={{
                        loadingOverlay: DataGridLoader,
                    }}
                    disableColumnResize
                    sx={{
                        "& .MuiDataGrid-columnHeaderCheckbox": {
                            borderBottom: "unset !important"
                        },
                    }}
                />
            </div>
        </div>
    </>);
};

export default CompanyBulkInventoryUploadReport;
